import React, { useState } from "react";

import { Eye, X } from "lucide-react";

import { ReactComponent as DragHandleIcon } from "@/assets/svg/dots-grid.svg";
import { stripBundlePrefix } from "@/utils/bundle-name";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { Button } from "@components/ui/button";
import { Card } from "@components/ui/card";
import { FileIcon } from "@components/ui/file-icon";

import { useObjectUrlPool } from "@hooks/useObjectUrlPool";

import { getFileUrl } from "@utils/file";
import { cn } from "@utils/ui";

import { FilePreviewDialog } from "./FilePreviewDialog";

type MergeFilesListProps = {
  files: DocSorterFileSchema[];
  mode: "edit" | "preview";
  draggedIndex?: number | null;
  onDragStart?: (index: number) => void;
  onDragOver?: (e: React.DragEvent, index: number) => void;
  onDragEnd?: () => void;
  onRemove?: (index: number) => void;
};

export const MergeFilesList = ({
  files,
  mode,
  draggedIndex,
  onDragStart,
  onDragOver,
  onDragEnd,
  onRemove,
}: MergeFilesListProps) => {
  if (mode === "edit") {
    return (
      <div className="space-y-2">
        {files.map((file, index) => (
          <Card
            key={file.id}
            draggable
            onDragStart={() => onDragStart?.(index)}
            onDragOver={(e) => onDragOver?.(e, index)}
            onDragEnd={onDragEnd}
            className={cn(
              "cursor-move flex-row items-center gap-2 p-4 transition-all",
              {
                "opacity-50": draggedIndex === index,
              }
            )}
          >
            <DragHandleIcon className="size-4 flex-shrink-0 text-foreground-muted/80 transition-colors hover:text-foreground" />

            <div className="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-success-800 text-xs font-medium text-primary-foreground">
              {index + 1}
            </div>

            <FileIcon
              fileName={stripBundlePrefix(file.name)}
              className="size-5 flex-shrink-0"
            />

            <div className="min-w-0 flex-1 overflow-hidden">
              <p
                className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium"
                title={stripBundlePrefix(file.name)}
              >
                {stripBundlePrefix(file.name)}
              </p>
              <p
                className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-foreground-muted"
                title={file.classification}
              >
                {file.classification || "Uncategorized"}
              </p>
            </div>

            <div className="ml-auto flex flex-shrink-0 items-center gap-1">
              <FilePreviewButton file={file} />
              <Button
                variant="ghost"
                size="sm"
                className="size-5 p-0 text-foreground-muted hover:bg-transparent hover:text-destructive"
                onClick={() => onRemove?.(index)}
              >
                <X className="size-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {files.map((file, index) => (
        <Card key={file.id} className="flex flex-row items-center gap-2 p-4">
          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
            {index + 1}
          </div>
          <FileIcon
            fileName={stripBundlePrefix(file.name)}
            className="h-6 w-6 flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p
              className="truncate text-xs font-medium"
              title={stripBundlePrefix(file.name)}
            >
              {stripBundlePrefix(file.name)}
            </p>
          </div>
          <div className="ml-auto flex flex-shrink-0 items-center">
            <FilePreviewButton file={file} />
          </div>
        </Card>
      ))}
    </div>
  );
};

type FilePreviewButtonProps = {
  file: DocSorterFileSchema;
};

const FilePreviewButton = ({ file }: FilePreviewButtonProps) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const objectUrls = useObjectUrlPool();
  const [fileType, setFileType] = useState<string | null>(null);

  const handleOpenChange = async (isOpen: boolean) => {
    if (isOpen && !fileUrl) {
      try {
        const result = await getFileUrl(file);
        if (result.url && result.type) {
          // MDRS-503: release the previously previewed document's bytes.
          objectUrls.set("preview", result.url);
          setFileUrl(result.url);
          setFileType(result.type);
        }
      } catch (error) {
        console.error(`Failed to fetch URL for file ${file.id}:`, error);
      }
    }
  };

  return (
    <FilePreviewDialog
      fileName={file.name}
      fileUrl={fileUrl}
      fileType={fileType}
      file={file}
      showSummaryOption={true}
      onOpenChange={handleOpenChange}
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className="size-5 p-0 text-foreground-muted hover:bg-transparent hover:text-primary"
          onClick={(e) => e.stopPropagation()}
        >
          <Eye className="size-4" />
        </Button>
      }
    />
  );
};
